// src/components/coaching/MaterialModal.jsx
import React, { useState, useEffect } from 'react';
import { XMarkIcon, DocumentTextIcon, PlusIcon } from '@heroicons/react/24/solid';
import CommentSection from '../comments/CommentSection';
import UrlConfig from '../../data/UrlConfig';
import { getApi, uploadApi } from '../../services/apiService';

const MaterialModal = ({ material, user, onClose }) => {
    // uploadedFiles ahora almacena arrays de archivos por cada asset ID: { [assetId]: [{url, name}, ...] }
    const [uploadedFiles, setUploadedFiles] = useState({});
    const [uploading, setUploading] = useState(null);

    // 1. Cargar todos los archivos al abrir el modal
    useEffect(() => {
        const fetchFiles = async () => {
            if (!material?.assets) return;
            const newFiles = {};
            
            for (let asset of material.assets) {
                if (asset.allowToUploadFile) {
                    try {
                        const data = await getApi(UrlConfig.get_files, {
                            materialId: material.id,
                            assetIndex: asset.id
                        });
                        
                        if (data.success && Array.isArray(data.files)) {
                            // Guardamos el array completo
                            newFiles[asset.id] = data.files;
                        }
                    } catch (err) {
                        console.error(`Error cargando archivos para ${asset.id}:`, err);
                    }
                }
            }
            setUploadedFiles(newFiles);
        };

        fetchFiles();
    }, [material]);

    if (!material) return null;

    const handleFileUpload = async (event, assetId) => {
        const file = event.target.files[0];
        if (!file) return;

        setUploading(assetId);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('materialId', material.id);
        formData.append('assetIndex', assetId);
        formData.append('username', user.username);

        try {
            const result = await uploadApi(UrlConfig.upload_file, formData);
            
            // En tu MaterialModal.jsx dentro de handleFileUpload
            if (result.success) {
                setUploadedFiles(prev => ({
                    ...prev,
                    [assetId]: [...(prev[assetId] || []), { 
                        url: result.fileUrl, 
                        name: result.name || file.name // Usamos el nombre que retorna el PHP, o el del objeto file como respaldo
                    }]
                }));
            } else {
                alert('Error al subir: ' + (result.message || 'Error desconocido'));
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error de conexión al subir el archivo.');
        } finally {
            setUploading(null);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm overflow-y-auto">
            <div className="bg-gray-950 border border-gray-800 w-full max-w-4xl p-6 rounded-2xl shadow-2xl relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white">
                    <XMarkIcon className="w-8 h-8" />
                </button>
                
                <h2 className="text-3xl font-bold text-white mb-2">{material.title}</h2>
                <p className="text-gray-400 mb-8">{material.description}</p>
                
                <div className="space-y-8">
                    {material.assets.map((asset, index) => (
                        <div key={index} className="bg-black p-4 rounded-xl border border-gray-800">
                            <h4 className="text-indigo-400 font-semibold mb-3 uppercase tracking-wider text-sm">
                                {asset.label}
                            </h4>
                            
                            {asset.type === 'video' && <video src={asset.url} controls className="w-full rounded-lg" />}
                            {asset.type === 'audio' && <div className="py-4"><audio src={asset.url} controls className="w-full" /></div>}
                            {asset.type === 'pdf' && (
                                <a href={asset.url} target="_blank" rel="noopener noreferrer" 
                                   className="block text-center py-4 border-2 border-dashed border-gray-700 rounded-lg text-gray-300 hover:text-white hover:border-indigo-500 transition-all">
                                    View Original: {asset.label}
                                </a>
                            )}

                            {asset.allowToUploadFile && (
                                <div className="mt-6 pt-4 border-t border-gray-800">
                                    <h5 className="text-sm font-medium text-gray-400 mb-3">Your Files:</h5>
                                    
                                    {/* Lista de archivos subidos */}
                                    <div className="space-y-2 mb-4">
                                        {(uploadedFiles[asset.id] || []).map((file, idx) => (
                                            <a key={idx} href={file.url} target="_blank" rel="noopener noreferrer" 
                                               className="flex items-center gap-3 text-green-400 bg-green-900/10 p-3 rounded-lg border border-green-800 hover:bg-green-900/20 transition-all text-sm">
                                                <DocumentTextIcon className="w-5 h-5" />
                                                <span className="truncate">{file.name || `Uploaded File ${idx + 1}`}</span>
                                            </a>
                                        ))}
                                    </div>

                                    {/* Input siempre visible para subir nuevo archivo */}
                                    <div className="mt-2">
                                        <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer transition ${uploading === asset.id ? 'bg-gray-700 text-gray-400 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-500'}`}>
                                            <PlusIcon className="w-4 h-4" />
                                            {uploading === asset.id ? 'Uploading...' : 'Upload New File'}
                                            <input 
                                                type="file" 
                                                className="hidden"
                                                onChange={(e) => handleFileUpload(e, asset.id)} 
                                                disabled={uploading === asset.id}
                                            />
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <div className="border-t border-gray-800 mt-12 pt-6">
                    <h3 className="text-xl font-semibold text-white mb-4">Session Reflections</h3>
                    <CommentSection threadId={`coaching-${material.id}`} />
                </div>
            </div>
        </div>
    );
};

export default MaterialModal;